import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AlertController, IonicModule, NavController, PopoverController } from '@ionic/angular';
import * as _ from 'lodash';
import moment from 'moment';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { AvatarModule } from 'src/app/components/avatar/avatar.module';
import { CONVERSATION_STATUS } from 'src/app/constants';
import { formatConversationContent, getSlackLink, isUrl, transformMessage } from 'src/app/utils/utils';
import { Conversation } from 'src/models';
import { ColonToEmojiPipe } from 'src/pipes/colonToEmoji.pipe';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AnalyticsService } from 'src/services/analytics.service';
import { AuthService } from 'src/services/auth.service';
import { ConversationsService } from 'src/services/conversations.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TagsService } from 'src/services/tags.service';

import { ActionDropdownComponent, ActionDropdownType } from '../action-dropdown/action-dropdown.component';
import { SnoozeComponent } from '../snooze/snooze.component';
import { TextPopoverComponent } from '../text-popover/text-popover.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    AvatarModule,
    RouterModule,
    NgxTippyModule,
    SharedPipesModule,
    ColonToEmojiPipe,
  ],
  selector: 'app-conversation-list',
  templateUrl: './conversation-list.component.html',
  styleUrls: ['./conversation-list.component.scss'],
})
export class ConversationListComponent implements OnChanges {
  constructor(
    private conversationsService: ConversationsService,
    private searchablePopoverService: SearchablePopoverService,
    private analyticsService: AnalyticsService,
    private popoverCtrl: PopoverController,
    private navCtrl: NavController,
    private route: ActivatedRoute,
    private alertCtrl: AlertController,
    private tagsService: TagsService,
    public authService: AuthService,
  ) {}

  public selectedConversations: Partial<Conversation>[] = [];
  private selectedConversationMap: any = {};
  private preservedSelectionIds: Set<string> = new Set();
  public totalCount: number;

  @Input() conversations: Partial<Conversation>[] = [];
  @Input() searchText: string;
  @Input() limit = 25;
  @Input() sortDesc = true;
  @Input() sortField = 'createdAt';
  @Input() whereClauses: any[] = [];
  @Input() disableAssignment: boolean;
  @Input() disableSelection: boolean;
  @Input() routerPath: string;
  @Input() viewTag: string; // any custom identifier
  @Input() dragEnabled: boolean;
  @Input() showSummary: boolean;
  @Input() isSidebarMode: boolean;
  @Input() routerDirection: 'forward' | 'root' = 'forward';
  @Input() showEmptyState: boolean;
  @Input() unlinkingEnabled: boolean;
  @Input() showTags: boolean;
  @Input() rightClickEnabled: boolean;

  @Output() conversationSelected = new EventEmitter<{ conversation: Conversation; index: Number }>();
  @Output() conversationStartedDragging = new EventEmitter<Conversation>();
  @Output() conversationStoppedDragging = new EventEmitter<Conversation>();
  @Output() didUpdateConversationCount = new EventEmitter<any>();
  @Output() didUnlinkConversation = new EventEmitter<Conversation>();

  isLoading = false;
  hasMore = false;
  cursor: any;
  hasInitialized = false;
  select = [
    'id',
    'status',
    'channelId',
    'teamId',
    'communityId',
    'createdAt',
    'updatedAt',
    'lastActivityAt',
    'statusUpdatedAt',
    'friendlyId',
    'wasManuallyCreated',
    'category',
    'closedAt',
    'respondedAt',
    'responseTime',
    'responseTimeWorking',
    'resolutionTime',
    'resolutionTimeWorking',
    'priority',
    'notes',
    'wakeUpAt',
    'snoozedAt',
    'excludeAnalytics',
    'sourceType',
    'submitterUserId',
    'inboundEmailRuleId',
    'widgetId',
    'externalChatConnectionId',
    'assignedToUserId',
    'assignedToUser.id',
    'assignedToUser.name',
    'assignedToUser.email',
    'assignedToUser.photo',
    'tags.id',
    'tags.name',
    'initialMessageId',
    'initialMessage.ts',
    'initialMessage.sourceType',
    'initialMessage.timestamp',
    'initialMessage.userId',
    'initialMessage.subtype',
    'initialMessage.teamId',
    'initialMessage.botId',
    'initialMessage.botName',
    'initialMessage.parentId',
    'initialMessage.id',
    'initialMessage.content',
    'initialMessage.fullContent',
    'initialMessage.nlpResult',
    'initialMessage.blocks',
    'initialMessage.inbound',
    'latestMessage.ts',
    'latestMessage.threadTs',
    'latestMessage.isAutoresponse',
    'latestMessage.isPrivateExplanation',
    'latestTopLevelTs',
    'customer.id',
    'customer.name',
    'customer.tags.id',
    'customer.tags.name',
    'customer.slackChannel.name',
    'customer.slackTeams.icon',
    'summary',
    'slackChannel.id',
    'slackChannel.name',
    'externalTasks.status',
    'externalTasks.name',
    'externalTasks.url',
    'submitterUser.name',
    'submitterUser.photo',
    'submitterUser.email',
    // We only include these for the export... should find a better way to export
    'ticketType.id',
    'ticketType.name',
    'ticketTypeFields',
    'account.id',
    'account.name',
    'title',
    'metadata',
    'subStatusId',
    'subStatus.key',
    'subStatus.name',
    'projectId',
    'escalations.title',
    'escalations.status',
    'escalations.createdAt',
    'escalations.updatedAt',
    'escalations.acknowledgedAt',
    'escalations.resolvedAt',
  ];

  public isURL = isUrl;

  /**
   * Angular lifecycle hook - called when any @Input() changes
   * Automatically restores selections when conversations data refreshes via @Input
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['conversations'] && this.preservedSelectionIds.size > 0) {
      // Restore selections from preserved IDs when conversations input changes
      this.restoreSelectionsFromPreservedIds();
    }
  }

  public selectConversations(conversations: Partial<Conversation>[]) {
    this.selectedConversations = conversations;
    this.selectedConversations.forEach((c, i) => {
      this.didSelectConversation(c as Conversation, i);
    });
  }

  transformConversation(conversation: Partial<Conversation>) {
    let tasks = 0;

    conversation.externalTasks?.forEach((task) => {
      if (!task.isCompleted) {
        tasks++;
      }
    });

    return {
      ...conversation,
      initialMessage: conversation.initialMessage ? transformMessage(conversation.initialMessage) : undefined,
      _externalTaskTodoCount: tasks,
    };
  }

  async fetchConversations(firstPage?: boolean) {
    const options: any = {
      limit: this.limit,
      order: [this.sortField],
      descending: this.sortDesc,
      cursor: this.cursor,
      search: this.searchText || undefined,
      select: this.select,
    };

    if (this.whereClauses.length) {
      options.where = this.whereClauses;
    }

    const result = await this.conversationsService.list(options, true);

    result.data = result.data.map((conversation) => this.transformConversation(conversation));

    this.cursor = result.cursors.next;
    this.hasMore = result.cursors.hasNext;
    this.totalCount = result.totalCount;

    if (firstPage) {
      this.conversations = [...result.data];
    } else {
      this.conversations.push(...result.data);
    }

    this.didUpdateConversationCount.next({ viewTag: this.viewTag, count: result.totalCount });
    this.hasInitialized = true;

    // Restore selections after conversations are loaded (for internally managed conversations)
    if (this.preservedSelectionIds.size > 0) {
      this.restoreSelectionsFromPreservedIds();
    }
  }

  async addConversation(conversation: Partial<Conversation>) {
    const index = this.conversations.findIndex((c) => c.id === conversation.id);

    if (conversation) {
      const transformed = this.transformConversation(conversation);

      if (index === -1) {
        this.conversations.unshift(transformed);
      } else {
        this.conversations[index] = transformed;
      }
    }

    return true;
  }

  removeConversation(conversation: Partial<Conversation>) {
    const index = this.conversations.findIndex((c) => c.id === conversation.id);

    if (index !== -1) {
      this.conversations.splice(index, 1);
    }
  }

  async loadConversations() {
    this.isLoading = true;

    this.cursor = undefined;
    await this.fetchConversations(true);

    this.isLoading = false;
  }

  didClickConversation(conversation: Conversation, index: number) {
    if (this.isSidebarMode) {
      this.selectedConversationMap = { [conversation.id]: conversation };
      this.conversationSelected.next({ conversation, index });
    }

    this.cacheConversation(conversation);
  }

  cacheConversation(conversation: Conversation) {
    this.conversationsService.cachedConversation = conversation;
  }

  async nextPage() {
    this.isLoading = true;

    await this.fetchConversations();

    this.isLoading = false;
  }

  async viewNotes(conversation: Conversation) {
    const popover = await this.popoverCtrl.create({
      component: TextPopoverComponent,
      componentProps: {
        text: conversation.notes,
        title: 'Note',
        callback: async (text: string) => {
          conversation.notes = text;
          await this.conversationsService.update(conversation.id, { notes: text });
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async changeAssignee(conversation: Conversation) {
    this.searchablePopoverService.users({
      event,
      selectedUserId: conversation.assignedToUserId,
      callback: async (id: string) => {
        this.analyticsService.trackEvent('Conversation List', 'Change Assignee');

        this.isLoading = true;
        await this.conversationsService.update(conversation.id, { assignedToUserId: id });
        this.loadConversations();
      },
    });
  }

  displayDate(conversation: Conversation) {
    const sortableFields = ['createdAt', 'lastActivityAt', 'updatedAt'];
    const date =
      sortableFields.includes(this.sortField) && conversation[this.sortField]
        ? conversation[this.sortField]
        : conversation.createdAt;
    return moment(date).from(moment());
  }

  didSelectConversation(conversation: Conversation, index: number) {
    if (!this.selectedConversationMap[conversation.id]) {
      this.selectedConversationMap[conversation.id] = conversation;
    } else {
      delete this.selectedConversationMap[conversation.id];
    }

    this.selectedConversations = _.values(this.selectedConversationMap);
    this.conversationSelected.next({ conversation, index });
  }

  clearSelectedConversations() {
    this.selectedConversationMap = {};
    this.selectedConversations = [];
    this.preservedSelectionIds.clear();
  }

  /**
   * Restore selections from a set of conversation IDs
   * This is used to preserve selections after data refreshes
   */
  restoreSelections(selectedIds: Set<string>) {
    // Store the IDs for future restores when conversations input changes
    this.preservedSelectionIds = new Set(selectedIds);
    this.restoreSelectionsFromPreservedIds();
  }

  /**
   * Internal method to restore selections from the preserved IDs
   */
  private restoreSelectionsFromPreservedIds() {
    this.selectedConversationMap = {};

    // Rebuild selection map from current conversations that match the preserved IDs
    for (const conversation of this.conversations) {
      if (conversation.id && this.preservedSelectionIds.has(conversation.id)) {
        this.selectedConversationMap[conversation.id] = conversation;
      }
    }

    this.selectedConversations = _.values(this.selectedConversationMap);
  }

  isConversationSelected(id: string) {
    return !!this.selectedConversationMap[id];
  }

  onDragStart($event, conversation: Conversation) {
    this.conversationStartedDragging.next(conversation);
  }

  onDragEnd($event, conversation: Conversation) {
    this.conversationStoppedDragging.next(conversation);
  }

  conversationTrackBy(conversation: any) {
    return conversation.id;
  }

  tagTrackBy(tag: any) {
    return tag.id;
  }

  async viewTasks(conversation: Conversation) {
    const options: ActionDropdownType[] = conversation.externalTasks.map((t) => {
      return { label: `${t.isCompleted ? '[Done]' : '[Todo]'} ${t.name}`, icon: 'open-outline' };
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (idx: number) => {
          popover.dismiss();

          window.open(conversation.externalTasks[idx].url, '_blank');
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  changePriority(row: Conversation) {
    this.searchablePopoverService.priorities({
      event,
      selectedPriority: String(row.priority),
      callback: async (priority: string) => {
        this.analyticsService.trackEvent('Conversation List', 'Change Priority');

        row.priority = Number(priority);
        await this.conversationsService.update(row.id, { priority: Number(priority) });
      },
    });
  }

  changeStatus(row: Conversation) {
    this.searchablePopoverService.subStatuses({
      event,
      selectedSubStatusId: row.subStatusId,
      projectId: row.project?.id ?? row.projectId,
      callback: async (id: string) => {
        this.analyticsService.trackEvent('Conversation List', 'Change Status');

        await this.conversationsService.update(row.id, { subStatusId: id });
        this.loadConversations();
      },
    });
  }

  public manuallyRouteToConversationAtIndex(index: number) {
    const conversation = this.conversations[index];

    if (conversation) {
      this.navCtrl.navigateRoot([this.routerPath, conversation.id], {
        relativeTo: this.route,
        queryParamsHandling: 'merge',
      });
      this.didClickConversation(conversation as Conversation, index);
    }
  }

  unlinkConversation(conversation: Conversation) {
    this.didUnlinkConversation.next(conversation);
  }

  formatContent(conversation: Conversation, maxLength?: number) {
    return formatConversationContent(conversation, maxLength);
  }

  async rightClickHandler(event: MouseEvent, conversation: Conversation, index: number) {
    event.preventDefault();
    event.stopPropagation();

    const options: ActionDropdownType[] = [
      {
        label: 'Open in new tab',
        icon: 'open-outline',
        id: 'open',
      },
      {
        label: 'Open in Slack',
        icon: 'logo-slack',
        id: 'openInSlack',
      },
      {
        label: 'Copy link to conversation',
        icon: 'link-outline',
        id: 'copyLink',
      },
      {
        label: 'Copy title',
        icon: 'copy-outline',
        id: 'copyTitle',
      },
      {
        label: 'Copy conversation friendly ID',
        icon: 'copy-outline',
        id: 'copyFriendlyId',
      },
      {
        label: 'Edit title',
        icon: 'create-outline',
        id: 'rename',
      },
      {
        label: 'Change status...',
        icon: 'pulse-outline',
        id: 'status',
      },
      {
        label: 'Change assignee...',
        icon: 'person-outline',
        id: 'assignee',
      },
      {
        label: 'Change priority...',
        icon: 'cellular-outline',
        id: 'priority',
      },
      {
        label: 'Add tag...',
        icon: 'pricetag-outline',
        id: 'addTag',
      },
      {
        label: 'Snooze...',
        icon: 'alarm-outline',
        id: 'snooze',
      },
      {
        label: 'Delete',
        icon: 'trash-outline',
        id: 'delete',
        destructive: true,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (idx: number, label: string, id?: string) => {
          popover.dismiss();
          await this.handleRightClickAction(id, conversation, index, event);
        },
      },
      showBackdrop: true,
      event,
    });
    popover.present();
  }

  private async handleRightClickAction(actionId: string, conversation: Conversation, index: number, event: MouseEvent) {
    switch (actionId) {
      case 'open':
        this.openConversationInNewTab(conversation);
        break;
      case 'openInSlack':
        this.openConversationInSlack(conversation);
        break;
      case 'copyLink':
        this.copyConversationLink(conversation);
        break;
      case 'copyTitle':
        this.copyConversationTitle(conversation);
        break;
      case 'copyFriendlyId':
        this.copyConversationFriendlyId(conversation);
        break;
      case 'rename':
        this.renameConversation(conversation, event);
        break;
      case 'addTag':
        this.addTagToConversation(conversation, event);
        break;
      case 'assignee':
        this.changeAssignee(conversation);
        break;
      case 'status':
        this.changeStatus(conversation);
        break;
      case 'priority':
        this.changePriority(conversation);
        break;
      case 'snooze':
        this.snoozeConversation(conversation);
        break;
      case 'delete':
        this.deleteConversation(conversation);
        break;
    }
  }

  async renameConversation(conversation: Conversation, event: MouseEvent) {
    const popover = await this.popoverCtrl.create({
      component: TextPopoverComponent,
      componentProps: {
        text: conversation.title || '',
        title: 'Edit Title',
        placeholder: 'Enter title',
        callback: async (text: string) => {
          if (text && text.trim() !== conversation.title) {
            this.analyticsService.trackEvent('Conversation List', 'Edit Title');
            await this.conversationsService.update(conversation.id, { title: text.trim() });
            conversation.title = text.trim();
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async addTagToConversation(conversation: Conversation, event: MouseEvent) {
    this.searchablePopoverService.tags({
      event,
      callback: async (id: string, name: string, object: any) => {
        this.analyticsService.trackEvent('Conversation List', 'Add Tag');
        // Add the tag to the conversation using the tags service
        await this.tagsService.createConversationLinks(id, [conversation.id]);
        this.loadConversations();
      },
    });
  }

  async deleteConversation(conversation: Conversation) {
    const alert = await this.alertCtrl.create({
      header: 'Delete Conversation',
      message: `Are you sure you want to delete conversation #${conversation.friendlyId}? This action cannot be undone.`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            this.analyticsService.trackEvent('Conversation List', 'Delete Conversation');
            await this.conversationsService.delete(conversation.id);
            this.removeConversation(conversation);
          },
        },
      ],
    });
    alert.present();
  }

  async snoozeConversation(conversation: Conversation) {
    this.analyticsService.trackEvent('Conversation List', 'Snooze');

    const popover = await this.popoverCtrl.create({
      component: SnoozeComponent,
      showBackdrop: false,
    });

    popover.present();

    const { data } = await popover.onWillDismiss();

    if (data?.snoozeDate) {
      await this.conversationsService.update(conversation.id, {
        status: CONVERSATION_STATUS.ON_HOLD,
        snoozedAt: new Date(),
        wakeUpAt: data.snoozeDate.toDate(),
      });
      this.loadConversations();
    }
  }

  openConversationInNewTab(conversation: Conversation) {
    const url = `${window.location.origin}${this.routerPath}/${conversation.id}`;
    window.open(url, '_blank');
  }

  openConversationInSlack(conversation: Conversation) {
    this.analyticsService.trackEvent('Conversation List', 'Open Conversation in Slack');

    const slackUrl = getSlackLink(
      conversation.slackChannel?.teamId ?? conversation.teamId,
      conversation.initialMessage?.ts,
      conversation.threadTs,
      conversation.channelId,
      this.authService.tenant?.slackTeamUrl,
    );

    window.open(slackUrl);
  }

  async copyConversationLink(conversation: Conversation) {
    try {
      const url = `${window.location.origin}${this.routerPath}/${conversation.id}`;
      await navigator.clipboard.writeText(url);
      this.analyticsService.trackEvent('Conversation List', 'Copy Conversation Link');
    } catch (err) {
      console.error('Failed to copy conversation link:', err);
    }
  }

  async copyConversationTitle(conversation: Conversation) {
    try {
      const title = conversation.title || this.formatContent(conversation, 200);
      await navigator.clipboard.writeText(title);
      this.analyticsService.trackEvent('Conversation List', 'Copy Conversation Title');
    } catch (err) {
      console.error('Failed to copy conversation title:', err);
    }
  }

  async copyConversationFriendlyId(conversation: Conversation) {
    try {
      await navigator.clipboard.writeText(conversation.friendlyId);
      this.analyticsService.trackEvent('Conversation List', 'Copy Conversation Friendly ID');
    } catch (err) {
      console.error('Failed to copy conversation friendly ID:', err);
    }
  }
}
